import { useEffect, useRef } from 'react'
import { useDashboard } from '@/hooks/useDashboard'
import { useLanguage } from '@/hooks/useLanguage'
import { DREAMITY_LOCAL_SNAPSHOT_REPLACED_EVENT } from '@/services/dreamityLocalSnapshot'
import { t, translateProgressStatus } from '@/utils/i18n'
import { formatCurrency } from '@/utils/currency'
import Card from '@/components/common/Card/Card'
import EmptyState from '@/components/common/EmptyState/EmptyState'

import { IconChart, IconStar, IconClipboard, IconPin, IconMoney, IconTarget, IconPackage, IconCheckCircle, IconTrendUp, IconClock } from '@/components/icons/Icons'
import './Dashboard.css'

function Dashboard() {
  const { language } = useLanguage()
  const {
    overallStats,
    progressItems,
    fetchAllDashboardData,
  } = useDashboard()

  const hasFetchedRef = useRef(false)

  const refreshToday = () => {
    const today = new Date()
    const yyyy = today.getFullYear()
    const mm = String(today.getMonth() + 1).padStart(2, '0')
    const dd = String(today.getDate()).padStart(2, '0')
    const dateStr = `${yyyy}-${mm}-${dd}`
    fetchAllDashboardData(dateStr, dateStr)
  }

  useEffect(() => {
    if (hasFetchedRef.current) return
    hasFetchedRef.current = true
    refreshToday()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAllDashboardData])

  useEffect(() => {
    const onSnapshotReplaced = () => {
      refreshToday()
    }
    window.addEventListener(DREAMITY_LOCAL_SNAPSHOT_REPLACED_EVENT, onSnapshotReplaced)
    return () => {
      window.removeEventListener(DREAMITY_LOCAL_SNAPSHOT_REPLACED_EVENT, onSnapshotReplaced)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAllDashboardData])

  return (
    <div className="dashboard">


      {/* 1. 전체통계 카드 - 총 꿈 수, 총 계획 수, 총 아이템 수, 총 예산 */}
      <Card className="dashboard__summary-card dashboard__summary-card--top">
          <h3>{language === 'ko' ? '전체 통계' : 'Overall Statistics'}</h3>
          <div className="dashboard__summary-grid dashboard__summary-grid--four">
            <div className="dashboard__summary-item">
              <div className="dashboard__summary-icon"><IconStar size={24} /></div>
              <div className="dashboard__summary-content">
                <div className="dashboard__summary-label">{language === 'ko' ? '총 꿈 수' : 'Total Dreams'}</div>
                <div className="dashboard__summary-value">{overallStats.totalDreamCount}</div>
              </div>
            </div>
            <div className="dashboard__summary-item">
              <div className="dashboard__summary-icon"><IconClipboard size={24} /></div>
              <div className="dashboard__summary-content">
                <div className="dashboard__summary-label">{language === 'ko' ? '총 계획 수' : 'Total Plans'}</div>
                <div className="dashboard__summary-value">{overallStats.totalPlanCount}</div>
              </div>
            </div>
            <div className="dashboard__summary-item">
              <div className="dashboard__summary-icon"><IconPin size={24} /></div>
              <div className="dashboard__summary-content">
                <div className="dashboard__summary-label">{language === 'ko' ? '총 아이템 수' : 'Total Items'}</div>
                <div className="dashboard__summary-value">{overallStats.totalItemCount}</div>
              </div>
            </div>
            <div className="dashboard__summary-item">
              <div className="dashboard__summary-icon"><IconMoney size={24} /></div>
              <div className="dashboard__summary-content">
                <div className="dashboard__summary-label">{language === 'ko' ? '총 예산' : 'Total Budget'}</div>
                <div className="dashboard__summary-value">{formatCurrency(overallStats.totalBudget, language)}</div>
              </div>
            </div>
          </div>
        </Card>

      <div className="dashboard__content">
        <div className="dashboard__right dashboard__right--full">
          {/* 2. 꿈별 진행상황 카드 */}
          <Card className="dashboard__chart-card">
            <h3>{language === 'ko' ? '꿈별 진행 상황' : 'Dream Progress'}</h3>
            {progressItems.length > 0 ? (
              <div className="dashboard__progress-gauges">
                {progressItems.map((entry) => {
                  const pct = Math.min(100, Math.max(0, entry.completionPercent ?? 0))
                  const statusColor =
                    entry.progressStatus === '완료'
                      ? 'var(--color-success)'
                      : entry.progressStatus === '진행중'
                        ? 'var(--color-primary)'
                        : '#95A5A6'
                  const StatusIcon =
                    entry.progressStatus === '완료'
                      ? IconCheckCircle
                      : entry.progressStatus === '진행중'
                        ? IconTrendUp
                        : IconClock
                  return (
                    <div key={entry.dreamId} className="dashboard__progress-gauge-item">
                      <div className="dashboard__progress-gauge-header">
                        <span className="dashboard__progress-gauge-name">{entry.dreamName}</span>
                        <div className="dashboard__progress-gauge-badges">
                          <span
                            className="dashboard__progress-gauge-status dashboard__progress-gauge-status--icon"
                            style={{ color: statusColor }}
                            title={translateProgressStatus(entry.progressStatus, language)}
                          >
                            <StatusIcon size={14} />
                            <span>{translateProgressStatus(entry.progressStatus, language)}</span>
                          </span>
                          <span
                            className="dashboard__progress-gauge-value dashboard__progress-gauge-value--icon"
                            style={{ color: statusColor }}
                            title={`${pct.toFixed(1)}%`}
                          >
                            <IconChart size={14} />
                            <span>{pct.toFixed(1)}%</span>
                          </span>
                          <span className="dashboard__progress-gauge-plan-count" title={language === 'ko' ? `계획 ${entry.completedPlanCount}/${entry.planCount}` : `Plans ${entry.completedPlanCount}/${entry.planCount}`}>
                            <IconClipboard size={14} />
                            <span>{entry.completedPlanCount}/{entry.planCount}</span>
                          </span>
                        </div>
                      </div>
                      <div className="dashboard__progress-gauge-bar">
                        <div
                          className="dashboard__progress-gauge-fill"
                          style={{ width: `${pct}%`, backgroundColor: statusColor }}
                        />
                      </div>
                      {(entry.targetAmount > 0 || entry.itemAmount > 0) && (
                        <div className="dashboard__progress-gauge-meta dashboard__progress-gauge-meta--icons">
                          <span className="dashboard__progress-gauge-budget" title={language === 'ko' ? '예산' : 'Budget'}>
                            <IconTarget size={14} />
                            <span className="dashboard__progress-gauge-budget-label">{language === 'ko' ? '예산' : 'Budget'}</span>
                            {formatCurrency(entry.targetAmount, language)}
                          </span>
                          <span className="dashboard__progress-gauge-budget" title={language === 'ko' ? '아이템 예산' : 'Item budget'}>
                            <IconPackage size={14} />
                            <span className="dashboard__progress-gauge-budget-label">{language === 'ko' ? '아이템' : 'Item'}</span>
                            {formatCurrency(entry.itemAmount, language)}
                          </span>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            ) : (
              <EmptyState
                icon={<IconChart size={32} />}
                title={t('dashboard.noData', language)}
                description={t('dashboard.noChartDataDesc', language) || '데이터가 없습니다.'}
              />
            )}
          </Card>

        </div>
      </div>
    </div>
  )
}

export default Dashboard
